import { useAtom } from '@reatom/npm-react'
import React from 'react'
import styled, { css } from 'styled-components'

import { Numeric, Typography, useHapticFeedback } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui/currency-icon'
import { Star } from '@root/shared/ui-kit'

import { PairRank } from '../../pair-rank'
import { PairTickers } from '../../pair-tickers'
import { PairTrend } from '../../pair-trend'
import { PairListItemBalance } from '../pair-balance'

import { PairMarketCapacity } from './pair-list-item-market-cap'
import { PairListItemProvider } from './pair-list-item-provider'

import type { Pair } from '../../../model'
import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { WalletModel } from '@root/entities/wallet/atoms'

export type PairListItemProps = {
  active: boolean
  pair: PairModel
  wallet: WalletModel | undefined
  onFavorite: (pair: Pair.PairMinimal) => void
  onClick?: (pair: Pair.PairMinimal) => void
}

export const PairListItem: React.FC<PairListItemProps> = ({
  pair,
  active = false,
  onFavorite,
  onClick,
  wallet,
}) => {
  const haptics = useHapticFeedback()
  const handleFavoriteClick: React.MouseEventHandler = (e) => {
    e.stopPropagation()
    haptics.impactOccurred('light')
    onFavorite(pair)
  }
  const [favorite] = useAtom(pair.favorite)
  const [rank] = useAtom(pair.rank)
  const [price] = useAtom(pair.price)
  const [priceChange] = useAtom(pair.stats['1D'].priceChange)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)

  const handleClick = () => {
    if (onClick) {
      haptics.impactOccurred('light')
      onClick(pair)
    }
  }

  return (
    <PairListItemRoot onClick={handleClick} $active={active}>
      <IconContainer>
        <CurrencyIcon
          withNetwork
          size={32}
          auxIconSize={14}
          currency={pair.quote}
        />
      </IconContainer>
      <Main>
        <MainTop>
          <MainStart>
            <PairTickers
              quote={pair.quote}
              base={pair.base}
              premiumInfo={pair.premiumInfo}
            />
            <PairTrend changePercents={priceChange.toNumber()} />
            {rank ? <PairRank rank={rank} /> : undefined}
          </MainStart>
          <MainEnd>
            <Price>
              <Numeric
                value={price.priceNative}
                preset="currency-amount-short"
                currency={pair.base.ticker}
              />
            </Price>
          </MainEnd>
        </MainTop>
        <MainBottom>
          <PairListItemBalance pair={pair} wallet={wallet} />
          <PairListItemProvider provider={pair.provider} />
        </MainBottom>
      </Main>

      <MarketCapContainer>
        <PairMarketCapacity marketCap={marketCapUsd} />
      </MarketCapContainer>

      <FavoriteButton type="button" onClick={handleFavoriteClick}>
        <Star size={16} filled={favorite} />
      </FavoriteButton>
    </PairListItemRoot>
  )
}

export const PairListItemRoot = styled.div<{ $active?: boolean }>`
  height: 60px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  overflow: hidden;
  position: relative;

  border-radius: 12px;
  border: 1px solid var(--backgroundA-2);
  background: var(--background-2);
  padding-inline: 12px 16px;

  ${({ $active }) => {
    if ($active) {
      return css`
        outline: 1px solid rgba(255, 255, 255, 0.56);
      `
    }
  }}
`

const Main = styled.div`
  display: grid;
  padding-inline-end: 6px;
`

const MainTop = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
`
const MainStart = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  gap: 4px;
`
const MainEnd = styled.div`
  display: grid;
  justify-self: end;
`

const MainBottom = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`

const IconContainer = styled.div`
  display: grid;
  padding-inline-end: 6px;
`

const MarketCapContainer = styled.div`
  padding-inline: 6px 0;
  padding-block: 2px 2px;
  border-inline-start: 1px solid var(--background-4);
`

const Price = styled(Typography).attrs({
  variant: 'body4-bold',
  component: 'div',
})`
  color: var(--text-12);
  text-align: right;
  white-space: nowrap;
`

const FavoriteButton = styled.button`
  all: unset;
  cursor: pointer;
  display: grid;
  align-items: center;
  justify-items: center;

  position: absolute;
  right: 0;
  top: 4px;
`
